import React, { useState, memo, useCallback } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './JobList.module.scss';
import classNames from 'classnames/bind';
import { Link, Typography } from '@mui/material';
import Status from '~/components/jobs/status/Status';
import BaseTable from '~/components/common/base-table/BaseTable';
import { IJobs } from '~/utils/interface/job';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { dashboardActions } from '~/thunks/dashboard/dashboardSlice';
import { jobActions } from '~/thunks/job/jobSlice';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import { useNavigate } from 'react-router-dom';
import { adminRouteAbsolute } from '~/utils/constants/route';
import MenuOption from '~/components/jobs/menu-option/MenuOption';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PERMISSIONS } from '~/utils/constants/permission';
import usePermission from '~/hooks/usePermission';
import LinkAddress from '~/components/link-address/LinkAddress';

const cx = classNames.bind(styles);

type JobListProps = {
  onOpenEdit: (id: string) => void;
};

function JobList(props: JobListProps) {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation('admin/job');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const permissions = usePermission();
  //#endregion Declare Hook

  //#region Selector
  const { jobs, currentPage, totalPages, filter } = useAppSelector((state) => state.dashboard);
  //#endregion Selector

  //#region Declare State
  const headCells: Array<HeadCelType> = [
    {
      id: 'name',
      label: t(`properties.name`),
      sortable: true,
      searchable: true,
    },
    {
      id: 'customer',
      label: t(`properties.customer`),
      sortable: true,
      searchable: true,
    },
    {
      id: 'statusId',
      label: t(`properties.status`),
      sortable: true,
      searchable: true,
    },
    {
      id: 'type',
      label: t(`properties.type`),
      sortable: true,
      searchable: true,
    },
    {
      id: 'address',
      label: t(`properties.address`),
      sortable: true,
      searchable: true,
    },
    {
      id: 'date',
      label: t(`properties.date`),
      sortable: true,
      searchable: true,
    },
    {
      id: '',
      label: '',
      sortable: false,
      searchable: false,
    },
  ];
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(itemId);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(jobActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(jobActions.changeMenuSelectId(menuSelectedId));
      dispatch(jobActions.changeOpenAlertDelete(true));
    }
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      dashboardActions.setFilter({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      dashboardActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };
  //#endregion Handle Function

  return (
    <Box className={cx('job-list-container')}>
      <BaseTable<IJobs>
        data={jobs}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        renderCell={(job: IJobs) => {
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('project-name-container')}>
                  <Box className={cx('projectName')}>
                    <Link
                      onClick={() => navigate(adminRouteAbsolute.jobDetail.replace(':id', job.id))}
                      className='text-[#3A3F51] hover:text-[#007AFC]'
                    >
                      {job.name}
                    </Link>
                  </Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box className={cx('info-container')}>
                  <Box className={cx('name')}>{job?.customer.name}</Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box className={cx('status-container')}>
                  <Status variant={job?.statusId} />
                </Box>
              </TableCell>

              <TableCell>
                <Box className={cx('status-container')}>
                  <Box className={cx('name')}>{job?.type}</Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box className={cx('status-container')}>
                  <Box className={cx('name')}>
                    <LinkAddress address={job?.fullAddress ?? ''} />
                  </Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box className={cx('deadline-container')}>
                  <Box className='flex flex-1'>
                    <Typography className={cx('card-time')}>
                      {job?.createdAt ? dayjs(job.createdAt).format('DD MMM, YYYY') : ''}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={(event) => {
                      openOptionMenu(event, job.id);
                    }}
                  >
                    <MenuVerticalIcon className='w-[19px] h-[19px]' />
                  </IconButton>
                </Box>
              </TableCell>
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        enableEdit={permissions[PERMISSIONS.project_edit]}
        enableDelete={permissions[PERMISSIONS.project_delete]}
      />
    </Box>
  );
}

export default memo(JobList);
